import { useContext, isValidElement, FC } from 'react';
import Link from 'next/link';

import { SideNavContext } from '../SideNav';
import { layoutContext } from '@context/layout';
import { filter } from '@modules/react-children-utilities';
import { getCollisions } from '@modules/popover-helpers';

import styles from './NavItem.module.scss';
import Tooltip, { PRect } from '@components/Tooltip/Tooltip';
import { Skeleton } from '@ui-lib';

const NavIcon: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['nav-icon']}>{children}</span>
);

const NavIconText: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['nav-text']}>{children}</span>
);

interface NavItemProps {
  href: string;
  selected: boolean;
  label: string;
  isBeta?: boolean;
  highlightId?: string;
}

function tooltipPositionRight(triggerRect?: PRect | null, tooltipRect?: PRect | null) {
  if (!triggerRect || !tooltipRect) {
    return {};
  }

  const { bottom, top } = getCollisions(triggerRect, tooltipRect);
  const offsetX = 5;
  const offsetY = 3;
  const above = bottom && !top;

  return {
    left: triggerRect.left + triggerRect.width + offsetX,
    top: above
      ? triggerRect.top - tooltipRect.height + window.pageYOffset + offsetY
      : triggerRect.top + window.pageYOffset + offsetY,
  };
}

const NavItemDivider: FC<React.PropsWithChildren<unknown>> = () => {
  return <div className={styles['nav-item-divider']} />;
};

const NavItemLoadingPlaceholder: React.FC = () => {
  return (
    <li className={styles['nav-item-container']}>
      <div className={styles['nav-item']}>
        <Skeleton height={42.45} width="100%" />
      </div>
    </li>
  );
};

const NavItem: FC<React.PropsWithChildren<NavItemProps>> = ({
  children,
  href,
  label,
  selected,
  isBeta,
  highlightId,
}) => {
  const navIcon = filter(children, (child) => isValidElement(child) && child?.type === NavIcon);
  const navText = filter(children, (child) => isValidElement(child) && child?.type === NavIconText);

  const { state: navState } = useContext(layoutContext);
  const { onSelectItem } = useContext(SideNavContext);

  return (
    <Tooltip
      size="small"
      label={label}
      aria-label="Nav item label"
      type="dark-info"
      show={navState.collapsed}
      positioner={tooltipPositionRight}
      showTriangle={false}
    >
      <li data-highlight-id={highlightId} className={styles['nav-item-container']}>
        <div className={styles['nav-item']} data-collapsed={navState.collapsed && navState.minimized}>
          <Link href={href} legacyBehavior>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <a className={styles['nav-item-content']} onMouseUp={onSelectItem} data-selected={selected}>
              <div className={styles['icon-container']}>{!!navIcon && navIcon}</div>

              <div className={styles['text-container']}>{navText}</div>

              {isBeta && <div className={styles['beta-tag-container']}>Beta</div>}
            </a>
          </Link>
        </div>
      </li>
    </Tooltip>
  );
};

export { NavItem, NavIcon, NavIconText, NavItemDivider, NavItemLoadingPlaceholder };
